import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Check, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const PricingSection = ({ openPopup }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const plans = [
    {
      name: "Essentials",
      price: "$45",
      subtitle: "+ Federal Filing Fee",
      description: "Perfect for straightforward trademark registration with basic protection",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Search",
        "State E-Filing",
        "Trademark Secured"
      ],
      note: "Govt. filing fee not included",
      popular: false,
      buttonText: "Get Started"
    },
    {
      name: "Enhanced",
      price: "$149",
      subtitle: "+ Federal Filing Fee",
      description: "Comprehensive protection with enhanced search and monitoring",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter"
      ],
      note: "Govt. filing fee not included",
      popular: false,
      buttonText: "Choose Enhanced"
    },
    {
      name: "Deluxe",
      price: "$299",
      subtitle: "+ Federal Filing Fee",
      description: "Our most popular choice with dedicated support and complete documentation",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager"
      ],
      note: "Govt. filing fee not included",
      popular: true,
      recommended: true,
      buttonText: "Start with Deluxe"
    },
    {
      name: "Platinum",
      price: "$349",
      subtitle: "+ Federal Filing Fee",
      description: "Ultimate protection with priority service and comprehensive legal support",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager",
        "High Approval Rate",
        "Comprehensive Search Report",
        "High Priority Service",
        "Cease & Desist Letter"
      ],
      note: "Govt. filing fee not included",
      popular: false,
      buttonText: "Go Platinum"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % plans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + plans.length) % plans.length);
  };

  return (
    <section className="pricing-section" id="pricing">
      <div className="section-container">
        <div className="section-header-center">
          <span className="section-label">Transparent Pricing</span>
          <h2 className="section-title">
            Choose Your
            <span className="title-highlight"> Protection Plan</span>
          </h2>
          <p className="section-description">
            Flexible pricing packages designed to fit businesses of all sizes. 
            All plans include expert trademark filing and USPTO submission. 
            No hidden fees, just honest pricing.
          </p>
        </div>

        {/* Desktop Grid View */}
        <div className={`pricing-grid ${isMobile ? 'mobile-hidden' : ''}`}>
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className={`pricing-card ${plan.recommended ? 'recommended' : ''}`}
            >
              {plan.recommended && (
                <div className="pricing-badge">
                  <Star size={16} />
                  <span>Recommended</span>
                </div>
              )}
              
              <div className="pricing-header">
                <h3 className="pricing-plan-name">{plan.name}</h3>
                <div className="pricing-amount">
                  <span className="price">{plan.price}</span>
                  <span className="price-subtitle">{plan.subtitle}</span>
                </div>
                <p className="pricing-description">{plan.description}</p>
              </div>

              <div className="pricing-features">
                <div className="features-scroll-container">
                  <ul className="features-list">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <Check size={18} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pricing-footer">
                <p className="pricing-note">{plan.note}</p>
                <Button 
                  onClick={openPopup} 
                  className={`pricing-button ${plan.recommended ? 'recommended-button' : ''}`}
                  size="lg"
                >
                  {plan.buttonText}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Slider View */}
        {isMobile && (
          <div className="pricing-slider">
            <div className="pricing-card recommended">
              {plans[currentSlide].recommended && (
                <div className="pricing-badge">
                  <Star size={16} />
                  <span>Recommended</span>
                </div>
              )}
              
              <div className="pricing-header">
                <h3 className="pricing-plan-name">{plans[currentSlide].name}</h3>
                <div className="pricing-amount">
                  <span className="price">{plans[currentSlide].price}</span>
                  <span className="price-subtitle">{plans[currentSlide].subtitle}</span>
                </div>
                <p className="pricing-description">{plans[currentSlide].description}</p>
              </div>

              <div className="pricing-features">
                <div className="features-scroll-container">
                  <ul className="features-list">
                    {plans[currentSlide].features.map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <Check size={18} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pricing-footer">
                <p className="pricing-note">{plans[currentSlide].note}</p>
                <Button 
                  onClick={openPopup} 
                  className={`pricing-button ${plans[currentSlide].recommended ? 'recommended-button' : ''}`}
                  size="lg"
                >
                  {plans[currentSlide].buttonText}
                </Button>
              </div>
            </div>

            {/* Slider Controls */}
            <div className="slider-controls">
              <button onClick={prevSlide} className="slider-button" aria-label="Previous plan">
                <ChevronLeft size={24} />
              </button>
              <div className="slider-dots">
                {plans.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                    aria-label={`Go to plan ${index + 1}`}
                  />
                ))}
              </div>
              <button onClick={nextSlide} className="slider-button" aria-label="Next plan">
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="pricing-info">
          <p className="pricing-info-text">
            <strong>Note:</strong> All packages require USPTO filing fees ($250-$350 per class) 
            paid directly to the government. We'll guide you through the complete cost breakdown 
            during your free consultation.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
